import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SmsConsent = () => {
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleOptOut = async (e) => {
    e.preventDefault();
    
    // Here you would call your backend API to process opt-out
    // For now, just show success message
    setStatus({
      type: 'success',
      message: 'You have been successfully opted out of SMS messages. You will no longer receive text messages from Miranda Landa Services LLC.'
    });
    
    setPhone('');
  };

  return (
    <>
      <Helmet>
        <title>SMS Consent & Opt-Out - Miranda Landa Services LLC</title>
        <meta name="description" content="Manage your SMS preferences. Opt-in or opt-out of text message communications from Miranda Landa Services LLC." />
      </Helmet>

      <section style={{
        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
        color: 'white',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{maxWidth: '800px', margin: '0 auto'}}>
          <h1 style={{fontSize: '2.5rem', fontWeight: '800', marginBottom: '1rem'}}>
            SMS Consent & Opt-Out
          </h1>
          <p style={{fontSize: '1.1rem', opacity: 0.95}}>
            Manage your text message preferences
          </p>
        </div>
      </section>

      <section style={{padding: '4rem 2rem', background: 'white'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          
          {/* TCPA Consent Information */}
          <div style={{
            background: '#fef3c7',
            padding: '2rem',
            borderRadius: '12px',
            border: '2px solid #f59e0b',
            marginBottom: '3rem'
          }}>
            <h2 style={{fontSize: '1.8rem', fontWeight: '700', color: '#92400e', marginBottom: '1rem'}}>
              📱 TCPA Consent Notice
            </h2>
            <p style={{fontSize: '1rem', color: '#78350f', lineHeight: '1.8'}}>
              By providing your mobile phone number and opting in to receive SMS messages from Miranda Landa Services LLC, 
              you expressly consent to receive automated marketing text messages at the number provided. Consent is not a 
              condition of purchase. Message and data rates may apply. Message frequency varies.
            </p>
          </div>

          {/* What You'll Receive */}
          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '1.8rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              What SMS Messages You'll Receive
            </h2>
            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {[
                { icon: '✅', title: 'Consultation Confirmations', desc: 'Confirmation of your consultation request' },
                { icon: '📅', title: 'Appointment Reminders', desc: 'Reminders for scheduled consultations' },
                { icon: '📊', title: 'Funding Updates', desc: 'Updates about your funding preparation process' },
                { icon: '📄', title: 'Document Requests', desc: 'Notifications when additional documents are needed' },
                { icon: '💡', title: 'Financing Guidance', desc: 'Tips and guidance for your funding journey' }
              ].map((item, index) => (
                <div key={index} style={{
                  background: '#f0fdf4',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'flex-start'
                }}>
                  <span style={{fontSize: '2rem'}}>{item.icon}</span>
                  <div>
                    <h3 style={{fontSize: '1.2rem', fontWeight: '600', color: '#064e3b', marginBottom: '0.5rem'}}>
                      {item.title}
                    </h3>
                    <p style={{color: '#065f46', margin: 0}}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Messages */}
          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '1.8rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              Sample Messages
            </h2>
            <div style={{
              background: '#f9fafb',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{display: 'grid', gap: '1rem'}}>
                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  borderLeft: '3px solid #10b981'
                }}>
                  <p style={{color: '#4b5563', fontSize: '0.95rem', margin: 0}}>
                    💬 "Thank you for contacting Miranda Landa Services LLC. Your consultation request is received. Reply STOP to opt out."
                  </p>
                </div>
                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  borderLeft: '3px solid #10b981'
                }}>
                  <p style={{color: '#4b5563', fontSize: '0.95rem', margin: 0}}>
                    💬 "Your funding assessment is ready. A consultant will contact you shortly. Reply STOP to unsubscribe."
                  </p>
                </div>
                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  borderLeft: '3px solid #10b981'
                }}>
                  <p style={{color: '#4b5563', fontSize: '0.95rem', margin: 0}}>
                    💬 "Your financial strategy session is scheduled for tomorrow at 2 PM. Reply STOP to cancel updates."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How to Opt Out */}
          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '1.8rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              How to Opt Out of SMS Messages
            </h2>
            
            <div style={{display: 'grid', gap: '2rem'}}>
              <div style={{
                background: '#f0fdf4',
                padding: '2rem',
                borderRadius: '12px',
                borderLeft: '4px solid #10b981'
              }}>
                <h3 style={{fontSize: '1.3rem', fontWeight: '700', color: '#064e3b', marginBottom: '1rem'}}>
                  Method 1: Text STOP
                </h3>
                <p style={{color: '#065f46', lineHeight: '1.7', marginBottom: '1rem'}}>
                  Reply with any of these keywords to any message you receive from us:
                </p>
                <div style={{
                  background: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#059669',
                  textAlign: 'center'
                }}>
                  STOP | END | CANCEL | UNSUBSCRIBE | QUIT
                </div>
                <p style={{color: '#065f46', lineHeight: '1.7', marginTop: '1rem'}}>
                  You will receive a confirmation message that you have been unsubscribed.
                </p>
              </div>

              <div style={{
                background: '#f0fdf4',
                padding: '2rem',
                borderRadius: '12px',
                borderLeft: '4px solid #10b981'
              }}>
                <h3 style={{fontSize: '1.3rem', fontWeight: '700', color: '#064e3b', marginBottom: '1rem'}}>
                  Method 2: Use This Form
                </h3>
                <form onSubmit={handleOptOut}>
                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem'}}>
                      Enter Your Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  
                  {status.message && (
                    <div style={{
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      background: status.type === 'success' ? '#d1fae5' : '#fee2e2',
                      color: status.type === 'success' ? '#065f46' : '#991b1b',
                      border: `1px solid ${status.type === 'success' ? '#10b981' : '#ef4444'}`
                    }}>
                      {status.message}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    style={{
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      color: 'white',
                      padding: '1rem 2rem',
                      borderRadius: '8px',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      border: 'none',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    Opt Out of SMS Messages
                  </button>
                </form>
              </div>

              <div style={{
                background: '#f0fdf4',
                padding: '2rem',
                borderRadius: '12px',
                borderLeft: '4px solid #10b981'
              }}>
                <h3 style={{fontSize: '1.3rem', fontWeight: '700', color: '#064e3b', marginBottom: '1rem'}}>
                  Method 3: Email Us
                </h3>
                <p style={{color: '#065f46', lineHeight: '1.7', marginBottom: '1rem'}}>
                  Send an email to:
                </p>
                <a 
                  href="mailto:sms@mirandaconnects.com?subject=SMS Opt-Out Request"
                  style={{
                    display: 'block',
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    color: '#059669',
                    fontWeight: '600',
                    textAlign: 'center',
                    fontSize: '1.1rem'
                  }}
                >
                  📧 sms@mirandaconnects.com
                </a>
                <p style={{color: '#065f46', lineHeight: '1.7', marginTop: '1rem'}}>
                  Include your phone number in the email, and we'll process your opt-out request within 24 hours.
                </p>
              </div>
            </div>
          </div>

          {/* Help & Support */}
          <div style={{marginBottom: '3rem'}}>
            <h2 style={{fontSize: '1.8rem', fontWeight: '700', color: '#064e3b', marginBottom: '1.5rem'}}>
              Need Help?
            </h2>
            <div style={{
              background: '#f9fafb',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{color: '#4b5563', lineHeight: '1.7', marginBottom: '1rem'}}>
                For questions about SMS messages or to get help:
              </p>
              <ul style={{color: '#4b5563', lineHeight: '1.8', paddingLeft: '2rem'}}>
                <li>Text <strong>HELP</strong> to any message from us</li>
                <li>Email: <a href="mailto:support@mirandaconnects.com" style={{color: '#059669'}}>support@mirandaconnects.com</a></li>
                <li>Visit our <Link to="/sms-policy" style={{color: '#059669'}}>SMS Policy</Link> page</li>
              </ul>
            </div>
          </div>

          {/* Important Notes */}
          <div style={{
            background: '#fef3c7',
            padding: '2rem',
            borderRadius: '12px',
            border: '2px solid #f59e0b'
          }}>
            <h3 style={{fontSize: '1.3rem', fontWeight: '700', color: '#92400e', marginBottom: '1rem'}}>
              Important Information
            </h3>
            <ul style={{color: '#78350f', lineHeight: '1.8', paddingLeft: '2rem'}}>
              <li>Opt-out requests are processed immediately</li>
              <li>You will receive one final confirmation message after opting out</li>
              <li>Message and data rates may apply based on your carrier plan</li>
              <li>We only send messages to numbers that have explicitly opted in</li>
              <li>Your consent is not required to use our services</li>
            </ul>
          </div>

          <div style={{marginTop: '3rem', textAlign: 'center'}}>
            <Link to="/sms-policy" style={{
              background: 'white',
              color: '#059669',
              padding: '1rem 2rem',
              borderRadius: '50px',
              fontWeight: '700',
              fontSize: '1rem',
              border: '2px solid #059669',
              display: 'inline-block',
              marginRight: '1rem'
            }}>
              View Full SMS Policy
            </Link>
            <Link to="/contact" style={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '50px',
              fontWeight: '700',
              fontSize: '1rem',
              display: 'inline-block'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default SmsConsent;
